import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, CheckCircle2, Link2, ExternalLink, BookOpen } from "lucide-react";

const PRESETS = {
  gmail: {
    label: "Gmail / Google Workspace",
    imap_host: "imap.gmail.com", imap_port: 993,
    smtp_host: "smtp.gmail.com", smtp_port: 465,
    smtp_ssl: true, smtp_starttls: false,
    help: "Necesitas una 'Contraseña de aplicación' si tienes verificación en 2 pasos.",
    helpUrl: "https://myaccount.google.com/apppasswords",
    instructions: [
      { step: "Activa la verificación en 2 pasos (si no la tienes)", detail: "Entra en myaccount.google.com/security y actívala (necesitarás tu móvil)." },
      { step: "Genera una contraseña de aplicación", detail: "Ve a myaccount.google.com/apppasswords, escribe el nombre 'Reservas Viaja de Verdad' y pulsa Crear." },
      { step: "Copia las 16 letras que te dé Google", detail: "Tipo 'xyab cdef ghij klmn'. Pégala en el campo de contraseña de este formulario (no uses tu contraseña habitual de Gmail)." },
      { step: "Pulsa 'Probar conexión' y luego 'Conectar'", detail: "Si todo está bien verás un check verde. La contraseña se guardará cifrada." },
    ],
  },
  outlook: {
    label: "Outlook / Microsoft 365",
    imap_host: "outlook.office365.com", imap_port: 993,
    smtp_host: "smtp.office365.com", smtp_port: 587,
    smtp_ssl: false, smtp_starttls: true,
    help: "Para Microsoft 365 con MFA, genera una contraseña de aplicación.",
    helpUrl: "https://account.microsoft.com/security",
    instructions: [
      { step: "Asegúrate de tener MFA activado", detail: "En tu cuenta Microsoft, en Seguridad → Opciones avanzadas." },
      { step: "Genera una 'Contraseña de aplicación'", detail: "En la misma sección de Seguridad → 'Crear una nueva contraseña de aplicación'." },
      { step: "Cópiala y pégala en este formulario", detail: "Usa esa contraseña en lugar de la habitual de Outlook." },
      { step: "Pulsa 'Probar conexión' y 'Conectar'", detail: "Comprobamos contra outlook.office365.com:587 con STARTTLS." },
    ],
  },
  ionos: {
    label: "IONOS / 1&1",
    imap_host: "imap.ionos.es", imap_port: 993,
    smtp_host: "smtp.ionos.es", smtp_port: 465,
    smtp_ssl: true, smtp_starttls: false,
    help: "Usa la contraseña de tu correo IONOS.",
    helpUrl: null,
    instructions: [
      { step: "Usa tu email y contraseña de IONOS directamente", detail: "IONOS acepta SMTP con la contraseña principal del correo, no necesitas generar otra." },
      { step: "Si tienes 2FA, créate una contraseña SMTP en IONOS", detail: "En tu panel de IONOS → Email → Configuración SMTP/IMAP." },
      { step: "Pulsa 'Probar conexión' y 'Conectar'", detail: "Si tu servidor IONOS es distinto a smtp.ionos.es ajústalo abajo." },
    ],
  },
  zoho: {
    label: "Zoho Mail",
    imap_host: "imap.zoho.eu", imap_port: 993,
    smtp_host: "smtp.zoho.eu", smtp_port: 465,
    smtp_ssl: true, smtp_starttls: false,
    help: "Genera una contraseña de aplicación en Zoho.",
    helpUrl: "https://accounts.zoho.com/home#security/device_security",
    instructions: [
      { step: "Entra a accounts.zoho.com → Seguridad → Contraseñas de aplicación", detail: "Pulsa 'Generar nueva contraseña' y dale un nombre como 'Reservas'." },
      { step: "Copia la contraseña que Zoho te dé", detail: "Pégala en el campo de contraseña de este formulario." },
      { step: "Comprueba el dominio del servidor", detail: "Si tu Zoho es .com en vez de .eu, cambia smtp.zoho.eu por smtp.zoho.com (y lo mismo en IMAP)." },
      { step: "Pulsa 'Probar conexión' y 'Conectar'", detail: "" },
    ],
  },
  custom: {
    label: "Personalizado (otro proveedor)",
    imap_host: "", imap_port: 993,
    smtp_host: "", smtp_port: 465,
    smtp_ssl: true, smtp_starttls: false,
    help: "Introduce manualmente los datos de tu servidor IMAP/SMTP.",
    helpUrl: null,
    instructions: [
      { step: "Pide a tu proveedor los datos SMTP", detail: "Necesitas: servidor SMTP, puerto, si usa SSL o STARTTLS, y el usuario+contraseña." },
      { step: "Rellena los campos correspondientes", detail: "Comprueba que el SSL/STARTTLS coincide con lo que te dijeron." },
      { step: "Pulsa 'Probar conexión' antes de 'Conectar'", detail: "Si falla, prueba a cambiar el modo de cifrado (SSL ↔ STARTTLS) o el puerto." },
    ],
  },
};

export default function ConnectEmailModal({ open, onClose, currentUser, onConnected }) {
  const [provider, setProvider] = useState("gmail");
  const [form, setForm] = useState({
    display_name: currentUser?.name || "",
    email: "",
    password: "",
    ...PRESETS.gmail,
  });
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  const changeProvider = (v) => {
    setProvider(v);
    setForm((f) => ({ ...f, ...PRESETS[v] }));
  };

  const payload = () => ({
    provider,
    email: form.email,
    password: form.password,
    display_name: form.display_name,
    imap_host: form.imap_host || null,
    imap_port: Number(form.imap_port) || 993,
    smtp_host: form.smtp_host,
    smtp_port: Number(form.smtp_port) || 465,
    smtp_ssl: !!form.smtp_ssl,
    smtp_starttls: !!form.smtp_starttls,
    signature: form.signature || "",
  });

  const onTest = async () => {
    setTesting(true);
    try {
      await api.post("/email-account/test", payload());
      toast.success("Conexión correcta ✓");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en la conexión");
    } finally {
      setTesting(false);
    }
  };

  const onConnect = async () => {
    setSaving(true);
    try {
      await api.post("/email-account/connect", payload());
      toast.success("Cuenta conectada");
      onConnected?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al conectar");
    } finally {
      setSaving(false);
    }
  };

  const preset = PRESETS[provider];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Conectar cuenta de email</DialogTitle>
          <DialogDescription className="text-xs">
            Las credenciales se cifran en reposo y solo se usan para autenticarse contra tu servidor SMTP.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="vv-label">Proveedor</Label>
            <Select value={provider} onValueChange={changeProvider}>
              <SelectTrigger data-testid="smtp-provider-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                {Object.entries(PRESETS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {preset.instructions && (
            <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-4">
              <div className="flex items-center gap-2 mb-2">
                <BookOpen size={14} className="text-blue-700"/>
                <h4 className="text-sm font-semibold text-blue-900">Cómo conectar tu cuenta de {preset.label}</h4>
              </div>
              <ol className="space-y-2 text-[12.5px] text-gray-700 leading-relaxed">
                {preset.instructions.map((ins, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                    <div>
                      <div className="font-medium text-gray-900">{ins.step}</div>
                      {ins.detail && <div className="text-gray-600 text-[11.5px]">{ins.detail}</div>}
                    </div>
                  </li>
                ))}
              </ol>
              {preset.helpUrl && (
                <a href={preset.helpUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline mt-3 font-medium">
                  Abrir página de {preset.label} <ExternalLink size={11}/>
                </a>
              )}
            </div>
          )}

          <div>
            <Label className="vv-label">Nombre a mostrar</Label>
            <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} data-testid="smtp-name-input" placeholder="Eduardo Moratalla"/>
          </div>

          <div>
            <Label className="vv-label">Email *</Label>
            <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="tu@empresa.com" data-testid="smtp-email-input"/>
          </div>

          <div>
            <Label className="vv-label">Contraseña (o contraseña de aplicación) *</Label>
            <Input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="smtp-password-input"/>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="vv-label">IMAP host</Label>
              <Input value={form.imap_host || ""} onChange={(e) => setForm({ ...form, imap_host: e.target.value })} data-testid="smtp-imap-host-input"/>
            </div>
            <div>
              <Label className="vv-label">IMAP puerto</Label>
              <Input type="number" value={form.imap_port} onChange={(e) => setForm({ ...form, imap_port: e.target.value })}/>
            </div>
            <div>
              <Label className="vv-label">SMTP host *</Label>
              <Input required value={form.smtp_host} onChange={(e) => setForm({ ...form, smtp_host: e.target.value })} data-testid="smtp-host-input"/>
            </div>
            <div>
              <Label className="vv-label">SMTP puerto</Label>
              <Input type="number" value={form.smtp_port} onChange={(e) => setForm({ ...form, smtp_port: e.target.value })}/>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={!!form.smtp_ssl} onCheckedChange={(v) => setForm({ ...form, smtp_ssl: v, smtp_starttls: v ? false : form.smtp_starttls })}/>
              SMTP SSL/TLS implícito
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={!!form.smtp_starttls} onCheckedChange={(v) => setForm({ ...form, smtp_starttls: v, smtp_ssl: v ? false : form.smtp_ssl })}/>
              SMTP STARTTLS
            </label>
          </div>

          <div>
            <Label className="vv-label">Firma (opcional)</Label>
            <Textarea rows={5} value={form.signature || ""} onChange={(e) => setForm({ ...form, signature: e.target.value })} placeholder={`${currentUser?.name || ""}\nViaja de Verdad\n+34 ...`} data-testid="smtp-signature-textarea"/>
            <p className="text-[11px] text-gray-500 mt-1">Se añadirá automáticamente al final de cada correo que envíes.</p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={testing || saving}>Cancelar</Button>
          <Button type="button" variant="outline" onClick={onTest} disabled={testing || saving || !form.email || !form.password} data-testid="smtp-test-button">
            {testing ? <Loader2 size={14} className="mr-2 animate-spin"/> : <CheckCircle2 size={14} className="mr-2"/>}
            Probar conexión
          </Button>
          <Button type="button" onClick={onConnect} disabled={saving || !form.email || !form.password || !form.smtp_host} className="bg-blue-600 hover:bg-blue-700" data-testid="smtp-connect-button">
            {saving ? <Loader2 size={14} className="mr-2 animate-spin"/> : <Link2 size={14} className="mr-2"/>}
            Conectar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
